import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";

import { useAppTheme } from "@hooks/useAppTheme";
import { Box, TooltipWrapper } from "@memorang/ui";
import { ActivityIndicator, IconButton } from "react-native-paper";

import { MAX_TURN_TEXT_LENGTH } from "../../constants";
import type { MessageFieldHandle, MessageFieldProps } from "./types";

const LINE_HEIGHT = 20;
const MAX_TEXTAREA_HEIGHT = 110;

export const MessageField = forwardRef<MessageFieldHandle, MessageFieldProps>(
	(
		{
			value,
			onChangeText,
			onSubmit,
			onMicPress,
			transcribing,
			sendDisabled,
			placeholder,
		},
		ref,
	) => {
		const theme = useAppTheme();
		const textareaRef = useRef<HTMLTextAreaElement>(null);
		const [isFocused, setIsFocused] = useState(false);
		const [isMultiline, setIsMultiline] = useState(false);

		useImperativeHandle(ref, () => ({
			focus: () => textareaRef.current?.focus(),
		}));

		const canSend = !sendDisabled && value.trim().length > 0;
		const active = isFocused || value.length > 0;

		const adjustHeight = () => {
			const el = textareaRef.current;
			if (!el) {
				return;
			}
			el.style.height = `${LINE_HEIGHT}px`;
			const needsMultiline = el.scrollHeight > LINE_HEIGHT;
			setIsMultiline(needsMultiline);
			el.style.height = needsMultiline
				? `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`
				: `${LINE_HEIGHT}px`;
		};

		// Transcription fills the value programmatically (no onChange), so resize here.
		// biome-ignore lint/correctness/useExhaustiveDependencies: resize on value change only
		useEffect(adjustHeight, [value]);

		const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				onSubmit();
			}
		};

		const sendButton = (
			<IconButton
				icon="arrow-up"
				mode="contained"
				size={18}
				style={{ margin: 0 }}
				containerColor={theme.colors.primary}
				iconColor={theme.colors.onPrimary}
				disabled={!canSend}
				accessibilityLabel="Send"
				onPress={onSubmit}
			/>
		);

		return (
			// biome-ignore lint/a11y/useKeyWithClickEvents: click is a convenience to focus the textarea, which is itself keyboard-accessible
			<div
				onClick={() => textareaRef.current?.focus()}
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "stretch",
					minHeight: 48,
					boxSizing: "border-box",
					border: `1px solid ${active ? theme.colors.primary : theme.colors.outlineVariant}`,
					borderRadius: theme.roundness,
					backgroundColor: theme.colors.surface,
					position: "relative",
					cursor: "text",
				}}
			>
				<Box
					flex={1}
					minWidth={0}
					paddingLeft={12}
					paddingRight={84}
					justifyContent={isMultiline ? "flex-start" : "center"}
					paddingVertical={isMultiline ? 10 : 0}
				>
					<textarea
						ref={textareaRef}
						placeholder={placeholder}
						aria-label={placeholder}
						maxLength={MAX_TURN_TEXT_LENGTH}
						value={value}
						onChange={(e) => {
							onChangeText(e.target.value);
							requestAnimationFrame(adjustHeight);
						}}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						onKeyDown={handleKeyDown}
						style={{
							width: "100%",
							height: `${LINE_HEIGHT}px`,
							minHeight: `${LINE_HEIGHT}px`,
							maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
							margin: 0,
							padding: 0,
							border: "none",
							outline: "none",
							backgroundColor: "transparent",
							resize: "none",
							overflow: "auto",
							color: theme.colors.onSurface,
							fontSize: theme.fonts.bodyMedium.fontSize,
							lineHeight: `${LINE_HEIGHT}px`,
							fontFamily: theme.fonts.bodyMedium.fontFamily,
						}}
					/>
				</Box>
				<Box
					flexDirection="row"
					gap={4}
					alignItems={isMultiline ? "flex-end" : "center"}
					paddingBottom={isMultiline ? 6 : 0}
					style={{ position: "absolute", right: 6, top: 0, bottom: 0 }}
				>
					{transcribing ? (
						<Box
							width={32}
							height={32}
							alignItems="center"
							justifyContent="center"
						>
							<ActivityIndicator size={18} color={theme.colors.primary} />
						</Box>
					) : (
						<IconButton
							icon="microphone"
							size={20}
							style={{ margin: 0 }}
							iconColor={theme.colors.onSurfaceVariant}
							accessibilityLabel="Record"
							onPress={onMicPress}
						/>
					)}
					{sendDisabled ? (
						<TooltipWrapper title="Hang tight - you can send once the response finishes">
							{sendButton}
						</TooltipWrapper>
					) : (
						sendButton
					)}
				</Box>
			</div>
		);
	},
);

MessageField.displayName = "MessageField";
